import { Link as RouterLink } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { colours } from './playerStyles'

const bannerSx = {
  mb: 2,
  color: colours.text,
  border: `1px solid ${colours.border}`,
  bgcolor: '#FFFFFF',
  '& .MuiAlert-message': { fontSize: '1rem', lineHeight: 1.6, width: '100%' },
  '& .MuiAlert-icon': { color: colours.accent },
}

// CPD UK reviewers see every screen without the progress locks.
export const ReviewerBanner = ({ enrolmentId }) => (
  <Alert severity="info" sx={bannerSx}>
    <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1.5}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Reviewer mode: progress locks are off</Typography>
      <Button component={RouterLink} to={`/training/course/${enrolmentId}/bank`} variant="outlined" size="small">
        View question bank
      </Button>
    </Stack>
  </Alert>
)

const AssessmentBanner = ({ enrolmentId, text, label }) => (
  <Alert severity="info" sx={bannerSx}>
    <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, mb: 1 }}>{text}</Typography>
    <Button component={RouterLink} to={`/training/course/${enrolmentId}/assessment`} variant="contained">
      {label}
    </Button>
  </Alert>
)

// Shown to someone who has passed this course before and may go straight
// to the assessment.
export const TestOutBanner = ({ enrolmentId }) => (
  <AssessmentBanner
    enrolmentId={enrolmentId}
    text="You have completed this course before. You can take the assessment now, or work through the course first."
    label="Take the assessment now"
  />
)

// Shown away from the last screen once the assessment is open, so a learner
// who has been looking back over the course can go straight to it.
export const AssessmentReadyBanner = ({ enrolmentId }) => (
  <AssessmentBanner
    enrolmentId={enrolmentId}
    text="You have finished the course content. The assessment is ready when you are."
    label="Start the assessment"
  />
)

// After a failed attempt: the modules the learner has to open again before
// the assessment reopens.
export const RevisitBanner = ({ modules, onJump }) => (
  <Alert severity="info" sx={bannerSx}>
    <AlertTitle sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>Look at these modules again</AlertTitle>
    <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, mb: 1 }}>
      You did not pass the assessment this time. Open every screen of {modules.length === 1 ? 'this module' : 'these modules'}{' '}
      again, then you can retake the assessment.
    </Typography>
    <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0, alignItems: 'flex-start' }}>
      {modules.map(({ module, firstIndex }) => (
        <li key={module.id}>
          <Button variant="outlined" onClick={() => onJump(firstIndex)} sx={{ textAlign: 'left' }}>
            Go to: {module.title}
          </Button>
        </li>
      ))}
    </Stack>
  </Alert>
)

// A failed save never blocks the learner: the message sits above the
// navigation until it is retried or dismissed.
export const SaveNotice = ({ notice, onDismiss }) => (
  <Alert
    severity={notice.severity ?? 'error'}
    role="alert"
    onClose={onDismiss}
    sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-message': { fontSize: '1rem', lineHeight: 1.6, color: colours.text } }}
    action={
      notice.retry ? (
        <Button color="inherit" onClick={notice.retry}>
          Retry
        </Button>
      ) : undefined
    }
  >
    {notice.message}
  </Alert>
)
